/**
 * @file 框选功能组件
 * 右键拖动进行框选，框选后可在框选范围内任意位置拖动移动选中节点
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Core, NodeSingular, Position } from 'cytoscape';
import { getNodesInBoxSelection } from '../controller/node-interaction';

interface BoxSelectionProps {
  // cytoscape 实例
  cy: Core | null;
  // 框选完成回调 - 返回选中的节点和边界框
  onSelectionComplete?: (
    nodes: NodeSingular[],
    bounds: { x1: number; y1: number; x2: number; y2: number } | null
  ) => void;
  // 框选清除回调
  onSelectionClear?: () => void;
  // 是否要求整个节点都在框选区域内
  wholeNode?: boolean;
  // 外部清空触发
  clearTrigger?: number;
  // 外部设置的选中节点（用于粘贴后自动选中）
  externalSelectedNodes?: NodeSingular[];
  // 外部设置的选区边界（用于粘贴后显示框选框）
  externalBounds?: { x1: number; y1: number; x2: number; y2: number } | null;
}

interface SelectionBox {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

const BoxSelection: React.FC<BoxSelectionProps> = ({
  cy,
  onSelectionComplete,
  onSelectionClear,
  wholeNode = false,
  clearTrigger,
  externalSelectedNodes,
  externalBounds,
}) => {
  // 是否正在框选
  const [isSelecting, setIsSelecting] = useState(false);
  // 框选区域（屏幕坐标）
  const [selectionBox, setSelectionBox] = useState<SelectionBox | null>(null);
  // 选中的节点
  const [selectedNodes, setSelectedNodes] = useState<NodeSingular[]>([]);
  // 框选完成后的选区边界（用于拖动）
  const [selectionBounds, setSelectionBounds] = useState<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  } | null>(null);
  // 是否正在拖动选区
  const [isDraggingSelection, setIsDraggingSelection] = useState(false);
  // 拖动起始位置
  const dragStartRef = useRef<Position | null>(null);

  // 将屏幕坐标转换为画布坐标
  const screenToCanvas = useCallback(
    (x: number, y: number): Position => {
      if (!cy) return { x, y };
      const container = cy.container();
      if (!container) return { x, y };

      const rect = container.getBoundingClientRect();
      const pan = cy.pan();
      const zoom = cy.zoom();

      return {
        x: (x - rect.left - pan.x) / zoom,
        y: (y - rect.top - pan.y) / zoom,
      };
    },
    [cy]
  );

  // 将画布坐标转换为容器坐标（用于 DOM 绝对定位）
  const canvasToScreen = useCallback(
    (x: number, y: number): Position => {
      if (!cy) return { x, y };
      const container = cy.container();
      if (!container) return { x, y };

      const pan = cy.pan();
      const zoom = cy.zoom();

      return {
        x: x * zoom + pan.x,
        y: y * zoom + pan.y,
      };
    },
    [cy]
  );

  // 计算选中节点的边界框
  const calculateSelectionBounds = useCallback((nodes: NodeSingular[]) => {
    if (nodes.length === 0) return null;

    let x1 = Infinity,
      y1 = Infinity,
      x2 = -Infinity,
      y2 = -Infinity;

    nodes.forEach(node => {
      const bb = node.boundingBox({ includeLabels: false });
      x1 = Math.min(x1, bb.x1);
      y1 = Math.min(y1, bb.y1);
      x2 = Math.max(x2, bb.x2);
      y2 = Math.max(y2, bb.y2);
    });

    return { x1, y1, x2, y2 };
  }, []);

  // 处理右键按下开始框选
  const handleContextMenuStart = useCallback(
    (e: MouseEvent) => {
      if (!cy || e.button !== 2) return; // 只处理右键

      e.preventDefault();

      const container = cy.container();
      if (!container) return;
      const rect = container.getBoundingClientRect();

      // 检查是否点击在节点上，如果是则不框选
      const target = e.target as HTMLElement;
      if (target.closest('.cy-node')) return;

      setIsSelecting(true);
      setSelectionBox({
        startX: e.clientX - rect.left,
        startY: e.clientY - rect.top,
        endX: e.clientX - rect.left,
        endY: e.clientY - rect.top,
      });

      // 清除之前的选择
      setSelectedNodes([]);
      setSelectionBounds(null);
    },
    [cy]
  );

  // 处理鼠标移动更新框选区域
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isSelecting && selectionBox) {
        if (!cy) return;
        const container = cy.container();
        if (!container) return;
        const rect = container.getBoundingClientRect();
        setSelectionBox(prev =>
          prev
            ? {
                ...prev,
                endX: e.clientX - rect.left,
                endY: e.clientY - rect.top,
              }
            : null
        );
      } else if (
        isDraggingSelection &&
        dragStartRef.current &&
        selectedNodes.length > 0
      ) {
        // 拖动选区
        const currentPos = screenToCanvas(e.clientX, e.clientY);
        const dx = currentPos.x - dragStartRef.current.x;
        const dy = currentPos.y - dragStartRef.current.y;

        // 获取选中节点的 ID 集合，用于快速查找
        const selectedNodeIds = new Set(selectedNodes.map(n => n.id()));

        // 只移动"顶层"选中节点（父节点不在选中列表中的节点）
        // 子节点会自动跟随父节点移动，不需要手动移动
        // 这样可以避免子节点被移动两次导致的位置偏移问题
        selectedNodes.forEach(node => {
          const parentId = node.data('parent');
          // 如果父节点也在选中列表中，跳过该节点（它会自动跟随父节点移动）
          if (parentId && selectedNodeIds.has(parentId)) {
            return;
          }
          const pos = node.position();
          node.position({
            x: pos.x + dx,
            y: pos.y + dy,
          });
        });

        dragStartRef.current = currentPos;

        // 更新选区边界（使用函数式更新，避免闭包导致的偏移累积问题）
        setSelectionBounds(prev =>
          prev
            ? {
                x1: prev.x1 + dx,
                y1: prev.y1 + dy,
                x2: prev.x2 + dx,
                y2: prev.y2 + dy,
              }
            : prev
        );
      }
    },
    [
      cy,
      isSelecting,
      selectionBox,
      isDraggingSelection,
      selectedNodes,
      screenToCanvas,
    ]
  );

  // 处理鼠标松开完成框选
  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      if (isSelecting && selectionBox && cy) {
        const container = cy.container();
        if (!container) return;
        const rect = container.getBoundingClientRect();

        // 计算框选区域的画布坐标
        const startCanvas = screenToCanvas(
          selectionBox.startX + rect.left,
          selectionBox.startY + rect.top
        );
        const endCanvas = screenToCanvas(
          selectionBox.endX + rect.left,
          selectionBox.endY + rect.top
        );

        // 获取框选区域内的节点
        const nodes = getNodesInBoxSelection(
          cy,
          startCanvas,
          endCanvas,
          wholeNode
        );

        if (nodes.length > 0) {
          setSelectedNodes(nodes);

          // 高亮选中的节点
          nodes.forEach(node => node.addClass('box-selected'));

          // 计算选区边界
          const bounds = calculateSelectionBounds(nodes);
          setSelectionBounds(bounds);

          if (onSelectionComplete) {
            onSelectionComplete(nodes, bounds);
          }
        }

        setIsSelecting(false);
        setSelectionBox(null);
      }

      if (isDraggingSelection) {
        setIsDraggingSelection(false);
        dragStartRef.current = null;
      }
    },
    [
      isSelecting,
      selectionBox,
      cy,
      screenToCanvas,
      wholeNode,
      calculateSelectionBounds,
      onSelectionComplete,
      isDraggingSelection,
    ]
  );

  // 处理点击清除选择
  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (e.button !== 0) return; // 只处理左键
      if (selectedNodes.length === 0) return;

      // 检查是否点击在选区外
      if (selectionBounds) {
        const clickPos = screenToCanvas(e.clientX, e.clientY);
        if (
          clickPos.x < selectionBounds.x1 ||
          clickPos.x > selectionBounds.x2 ||
          clickPos.y < selectionBounds.y1 ||
          clickPos.y > selectionBounds.y2
        ) {
          // 清除选择
          selectedNodes.forEach(node => node.removeClass('box-selected'));
          setSelectedNodes([]);
          setSelectionBounds(null);
          if (onSelectionClear) {
            onSelectionClear();
          }
        }
      }
    },
    [selectedNodes, selectionBounds, screenToCanvas, onSelectionClear]
  );

  // 绑定事件
  useEffect(() => {
    if (!cy) return;

    const container = cy.container();
    if (!container) return;

    // 禁用默认右键菜单
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    // 使用捕获阶段，确保即使内部元素 stopPropagation 也能拦截到（如节点覆盖层）
    container.addEventListener('contextmenu', handleContextMenu, true);
    container.addEventListener('mousedown', handleContextMenuStart);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('click', handleClickOutside);

    return () => {
      container.removeEventListener('contextmenu', handleContextMenu, true);
      container.removeEventListener('mousedown', handleContextMenuStart);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('click', handleClickOutside);
    };
  }, [
    cy,
    handleContextMenuStart,
    handleMouseMove,
    handleMouseUp,
    handleClickOutside,
  ]);

  // 更新选区边界显示位置（响应画布 pan/zoom）
  useEffect(() => {
    if (!cy || !selectionBounds) return;

    const handleViewChange = () => {
      // 选区边界是画布坐标，不需要更新
      // 但需要触发重新渲染
      setSelectionBounds(bounds => (bounds ? { ...bounds } : null));
    };

    cy.on('pan zoom', handleViewChange);
    return () => {
      cy.off('pan zoom', handleViewChange);
    };
  }, [cy, selectionBounds]);

  // 外部清理信号
  useEffect(() => {
    if (clearTrigger === undefined) return;
    if (!cy) return;
    cy.$('.box-selected').removeClass('box-selected');
    setSelectedNodes([]);
    setSelectionBounds(null);
    setIsSelecting(false);
    setSelectionBox(null);
    if (onSelectionClear) onSelectionClear();
  }, [clearTrigger, cy, onSelectionClear]);

  // 同步外部设置的选中节点和边界（用于粘贴后显示框选框）
  useEffect(() => {
    if (
      externalSelectedNodes &&
      externalSelectedNodes.length > 0 &&
      externalBounds
    ) {
      setSelectedNodes(externalSelectedNodes);
      setSelectionBounds(externalBounds);
    }
  }, [externalSelectedNodes, externalBounds]);

  // 渲染框选矩形
  const renderSelectionRect = () => {
    if (!selectionBox) return null;

    const x = Math.min(selectionBox.startX, selectionBox.endX);
    const y = Math.min(selectionBox.startY, selectionBox.endY);
    const width = Math.abs(selectionBox.endX - selectionBox.startX);
    const height = Math.abs(selectionBox.endY - selectionBox.startY);

    return (
      <div
        className="box-selection-rect"
        style={{
          position: 'absolute',
          left: x,
          top: y,
          width,
          height,
          border: '1px dashed #1890ff',
          backgroundColor: 'rgba(24, 144, 255, 0.1)',
          pointerEvents: 'none',
          zIndex: 9999,
        }}
      />
    );
  };

  // 处理选区边界 div 的拖动开始
  const handleBoundsDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return; // 只处理左键

      e.preventDefault();
      e.stopPropagation();

      const clickPos = screenToCanvas(e.clientX, e.clientY);
      setIsDraggingSelection(true);
      dragStartRef.current = clickPos;
    },
    [screenToCanvas]
  );

  // 渲染选区边界（完成框选后）
  const renderSelectionBounds = () => {
    if (!selectionBounds || !cy) return null;

    const topLeft = canvasToScreen(selectionBounds.x1, selectionBounds.y1);
    const bottomRight = canvasToScreen(selectionBounds.x2, selectionBounds.y2);

    return (
      <div
        className="selection-bounds"
        onMouseDown={handleBoundsDragStart}
        style={{
          position: 'absolute',
          left: topLeft.x,
          top: topLeft.y,
          width: bottomRight.x - topLeft.x,
          height: bottomRight.y - topLeft.y,
          border: '2px dashed #1890ff',
          backgroundColor: 'transparent',
          cursor: 'move',
          pointerEvents: 'auto',
          zIndex: 999,
        }}
      />
    );
  };

  return (
    <>
      {isSelecting && renderSelectionRect()}
      {selectedNodes.length > 0 && renderSelectionBounds()}
    </>
  );
};

export default BoxSelection;
